import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, LayoutChangeEvent } from 'react-native';
import Svg, { Rect } from 'react-native-svg';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface BarChartPoint {
  label: string;
  value: number;
}

export interface BarChartCardProps {
  title?: string;
  data: BarChartPoint[];
  height?: number;
  color?: string;
}

/** Hand-rolled SVG vertical bar chart — same drawing-primitive approach as
 * AreaChartCard/DonutChart (no charting library installed in this codebase).
 * Bar labels render below each bar; truncated to 2 lines to survive longer
 * campaign/node names without breaking layout. */
export const BarChartCard: React.FC<BarChartCardProps> = ({ title, data, height = 180, color }) => {
  const styles = useThemedStyles(getStyles);
  const [width, setWidth] = useState(0);
  const barColor = color ?? (colors.brand.primary as string);

  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width);

  const { bars, maxValue } = useMemo(() => {
    if (data.length === 0 || width === 0) return { bars: [] as { x: number; y: number; w: number; h: number; value: number }[], maxValue: 0 };
    const padding = 8;
    const labelHeight = 30;
    const usableWidth = width - padding * 2;
    const usableHeight = height - labelHeight - padding;
    const max = Math.max(...data.map((d) => d.value), 1);
    const gap = 8;
    const barWidth = Math.max((usableWidth - gap * (data.length - 1)) / data.length, 4);

    const computed = data.map((d, i) => {
      const h = max > 0 ? (d.value / max) * usableHeight : 0;
      return {
        x: padding + i * (barWidth + gap),
        y: usableHeight - h,
        w: barWidth,
        h,
        value: d.value,
      };
    });

    return { bars: computed, maxValue: max };
  }, [data, width, height]);

  return (
    <View>
      {!!title && <Text style={styles.title}>{title}</Text>}
      <View style={{ height }} onLayout={onLayout}>
        {width > 0 && data.length > 0 ? (
          <>
            <Svg width={width} height={height - 30}>
              {bars.map((b, i) => (
                <Rect key={i} x={b.x} y={b.y} width={b.w} height={Math.max(b.h, 1)} rx={3} fill={barColor} />
              ))}
            </Svg>
            <View style={[styles.labelsRow, { width }]}>
              {data.map((d, i) => (
                <Text key={i} style={[styles.label, { width: bars[i]?.w ?? 0 }]} numberOfLines={2}>
                  {d.label}
                </Text>
              ))}
            </View>
          </>
        ) : (
          <View style={styles.empty}><Text style={styles.emptyText}>No data yet</Text></View>
        )}
      </View>
      {data.length > 0 && (
        <Text style={styles.footer}>{data.length} items · peak {maxValue}</Text>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: designTokens.spacing.sm,
  },
  labelsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
  },
  label: {
    fontSize: 9,
    color: colors.text.secondary,
    textAlign: 'center',
  },
  footer: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.medium,
    marginTop: designTokens.spacing.xs,
    textAlign: 'center',
  },
  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
});
